/**
 * Alerts API Service (v2)
 *
 * Wraps /api/v2/alerts. The legacy v1 PUT /alert is no longer called from
 * the frontend; the backend keeps it around for compatibility but new code
 * should use these v2 methods.
 */

import { api } from '../client';

export const alertsService = {
  /**
   * Manually create an alert. Admin-only on the backend (returns 403 otherwise).
   * Used by the focal-point picker on the Status page.
   *
   * @param {Object} payload
   * @param {string} payload.installation_id
   * @param {'leakage'|'offline'|'stale'} payload.type
   * @param {string} payload.timestamp - ISO 8601
   * @param {{x:number,y:number}} [payload.focus_point]
   * @param {string} [payload.severity]
   * @param {string} [payload.message]
   * @param {Array<Object>} [payload.measurements]
   * @returns {Promise<Object>}
   */
  async create(payload) {
    return await api.post('/api/v2/alerts', payload);
  },

  /**
   * List alerts. All filters are optional; admin sees all, members see only
   * alerts on installations they have project access to.
   *
   * @param {Object} [filters]
   * @param {string} [filters.installation_id]
   * @param {Array<'leakage'|'offline'|'stale'>} [filters.type]
   * @param {Array<'active'|'acknowledged'|'resolved'>} [filters.status]
   * @param {number} [filters.limit]
   * @param {number} [filters.offset]
   * @returns {Promise<Array>}
   */
  async list(filters = {}) {
    const params = new URLSearchParams();
    if (filters.installation_id) params.set('installation_id', filters.installation_id);
    if (filters.type && filters.type.length) params.set('type', filters.type.join(','));
    if (filters.status && filters.status.length) params.set('status', filters.status.join(','));
    if (filters.limit != null) params.set('limit', String(filters.limit));
    if (filters.offset != null) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return await api.get(`/api/v2/alerts${qs ? `?${qs}` : ''}`);
  },

  /**
   * Per-installation open-alert summary for all accessible installations.
   * @returns {Promise<Record<string, {worst_severity: string|null, count: number, types: string[]}>>}
   */
  async getSummary() {
    return await api.get('/api/v2/alerts/summary');
  },

  /** Acknowledge an alert. Idempotent. */
  async acknowledge(alertId) {
    return await api.post(`/api/v2/alerts/${alertId}/acknowledge`);
  },

  /** Manually resolve an alert. Idempotent. */
  async resolve(alertId) {
    return await api.post(`/api/v2/alerts/${alertId}/resolve`);
  },

  /** Undo a resolve. Idempotent. */
  async reopen(alertId) {
    return await api.post(`/api/v2/alerts/${alertId}/reopen`);
  },

  /** Hard-delete an alert. Admin only on the backend (returns 403 otherwise). */
  async delete(alertId) {
    return await api.delete(`/api/v2/alerts/${alertId}`);
  },
};
